import { customBaseUrl, httpGet, httpPost, httpPut, httpDelete } from '../store/http';

// ────────────────────────────────────────────────────────────────────────────
// Regulatory Reporting Service
// Base URL: customBaseUrl.logUrl
// ────────────────────────────────────────────────────────────────────────────

/**
 * Build a query string from params, ignoring null/undefined/empty values.
 */
const buildQuery = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
};

// ── Reports ─────────────────────────────────────────────────────────────────

/**
 * GET /api/v1/admin/regulatory-reports
 * Fetch all regulatory reports with optional filters & pagination.
 */
export const getRegulatoryReports = async (params = {}) => {
  const query = buildQuery(params);
  return await httpGet(`${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports${query}`);
};

/**
 * GET /api/v1/admin/regulatory-reports/:id
 * Fetch a single report by ID.
 */
export const getRegulatoryReportById = async (reportId) => {
  return await httpGet(`${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/${reportId}`);
};

/**
 * POST /api/v1/admin/regulatory-reports/generate
 * Generate a new regulatory report.
 */
export const generateRegulatoryReport = async (payload) => {
  return await httpPost(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/generate`,
    payload
  );
};

/**
 * POST /api/v1/admin/regulatory-reports/:id/submit
 * Submit a report to the regulator.
 */
export const submitReportToRegulator = async (reportId, payload = {}) => {
  return await httpPost(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/${reportId}/submit`,
    payload
  );
};

/**
 * POST /api/v1/admin/regulatory-reports/:id/approve
 * Approve a pending report.
 */
export const approveReport = async (reportId, payload = {}) => {
  return await httpPost(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/${reportId}/approve`,
    payload
  );
};

/**
 * POST /api/v1/admin/regulatory-reports/:id/reject
 * Reject a pending report.
 */
export const rejectReport = async (reportId, payload = {}) => {
  return await httpPost(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/${reportId}/reject`,
    payload
  );
};

/**
 * GET /api/v1/admin/regulatory-reports/:id/export
 * Export a single report (returns data for CSV/Excel/PDF export).
 */
export const exportRegulatoryReport = async (reportId, params = {}) => {
  const query = buildQuery(params);
  return await httpGet(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/${reportId}/export${query}`
  );
};

/**
 * GET /api/v1/admin/regulatory-reports/export
 * Bulk export filtered reports.
 */
export const exportRegulatoryReports = async (params = {}) => {
  const { page, size, ...rest } = params;
  const query = buildQuery(rest);
  return await httpGet(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/export${query}`
  );
};

/**
 * GET /api/v1/admin/regulatory-reports/stats
 * Fetch dashboard summary stats.
 */
export const getRegulatoryReportStats = async () => {
  return await httpGet(`${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/stats`);
};

// ── Schedules ───────────────────────────────────────────────────────────────

/**
 * GET /api/v1/admin/regulatory-reports/schedules
 * List all report schedules.
 */
export const getReportSchedules = async (params = {}) => {
  const query = buildQuery(params);
  return await httpGet(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/schedules${query}`
  );
};

/**
 * POST /api/v1/admin/regulatory-reports/schedules
 * Create a new scheduled report.
 */
export const createReportSchedule = async (payload) => {
  return await httpPost(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/schedules`,
    payload
  );
};

/**
 * PUT /api/v1/admin/regulatory-reports/schedules/:id
 * Update an existing schedule.
 */
export const updateReportSchedule = async (scheduleId, payload) => {
  return await httpPut(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/schedules/${scheduleId}`,
    payload
  );
};

/**
 * DELETE /api/v1/admin/regulatory-reports/schedules/:id
 * Delete a schedule.
 */
export const deleteReportSchedule = async (scheduleId) => {
  return await httpDelete(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/schedules/${scheduleId}`
  );
};

// ── Templates ───────────────────────────────────────────────────────────────

/**
 * GET /api/v1/admin/regulatory-reports/templates
 * List all report templates.
 */
export const getReportTemplates = async (params = {}) => {
  const query = buildQuery(params);
  return await httpGet(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/templates${query}`
  );
};

/**
 * POST /api/v1/admin/regulatory-reports/templates
 * Create a new report template.
 */
export const createReportTemplate = async (payload) => {
  return await httpPost(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/templates`,
    payload
  );
};

/**
 * PUT /api/v1/admin/regulatory-reports/templates/:id
 * Update an existing template.
 */
export const updateReportTemplate = async (templateId, payload) => {
  return await httpPut(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/templates/${templateId}`,
    payload
  );
};

// ── Audit Trail ─────────────────────────────────────────────────────────────

/**
 * GET /api/v1/admin/regulatory-reports/audit-trail
 * Fetch audit trail for all regulatory reporting activities.
 */
export const getReportAuditTrail = async (params = {}) => {
  const query = buildQuery(params);
  return await httpGet(
    `${customBaseUrl.logUrl}/api/v1/admin/regulatory-reports/audit-trail${query}`
  );
};
